import { useEffect, useMemo, useRef, useState } from "react";
import BillingHeader from "./BillingHeader";
import GlobalFilter from "../../Utility/GlobalFilter/GlobalFilter";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import { RiDeleteBinFill, RiPencilLine } from "react-icons/ri";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import Pagination from "../../Utility/Pagination/Pagination";
import TabMappingAddModel from "./AddMappingModels/TabMappingAddModel";
import AddButton from "../../Utility/AddButton/AddButton";
import { useDispatch, useSelector } from "react-redux";
import { selectTabMappingData, selectTabMappingLoading } from "../../Redux/Slices/tabMappingSlice";
import {
  createTabMapping,
  deleteTabMapping as deleteTabMappingApi,
  fetchTabMappings,
  updateTabMapping,
} from "../../Redux/Thunks/tabMappingThunk";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { selectSystemListItems } from "../../Redux/Slices/systemListsSlice";
import { mapSystemListItemToOption } from "../Issues/issueMappers";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { showError, showSuccess } from "../../utils/toast";
import { ContentLoader } from "../../Utility/Loader";
import { mappingListGlobalFilterProps } from "../../Utility/GlobalFilter/mappingListFilterDefaults";
import { getTabMappingsListFromApiData } from "./tabMappingDataUtils";

const EMPTY_TAB_FILTERS = {
  search: "",
  tag: "",
  account: "",
};

const TabMapping = () => {
  const [filters, setFilters] = useState(() => ({ ...EMPTY_TAB_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_TAB_FILTERS }));

  const dispatch = useDispatch();
  const tabMappingData = useSelector(selectTabMappingData);
  const loading = useSelector(selectTabMappingLoading);
  const hasFetchedRef = useRef(false);

  const clientTagItems = useSelector((s) => selectSystemListItems(s, "CLIENT_TAG"));
  const accountingItems = useSelector((s) => selectSystemListItems(s, "ACCOUNTING_TAG"));
  const clientTagList = useMemo(() => clientTagItems ?? [], [clientTagItems]);
  const accountingList = useMemo(() => accountingItems ?? [], [accountingItems]);

  const sourceTagOptions = useMemo(
    () => clientTagList.map((item) => mapSystemListItemToOption(item, {})),
    [clientTagList]
  );
  const accountingTagOptions = useMemo(
    () => accountingList.map((item) => mapSystemListItemToOption(item, {})),
    [accountingList]
  );

  const tagFilterOptions = useMemo(
    () => [{ value: "", label: "All" }, ...sourceTagOptions],
    [sourceTagOptions]
  );
  const accountFilterOptions = useMemo(
    () => [{ value: "", label: "All" }, ...accountingTagOptions],
    [accountingTagOptions]
  );

  const rawMappings = getTabMappingsListFromApiData(tabMappingData);

  const filteredMappings = useMemo(() => {
    let list = rawMappings;
    const f = appliedFilters;
    if (f.search?.trim()) {
      const q = f.search.toLowerCase();
      list = list.filter(
        (m) =>
          String(m.tag ?? "")
            .toLowerCase()
            .includes(q) ||
          String(m.account ?? "")
            .toLowerCase()
            .includes(q)
      );
    }
    if (f.tag) {
      list = list.filter((m) => String(m.sourceTagId) === String(f.tag));
    }
    if (f.account) {
      list = list.filter((m) => String(m.targetTagId) === String(f.account));
    }
    return list;
  }, [rawMappings, appliedFilters]);

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(filteredMappings, {
    defaultSortKey: "tag",
    defaultSortDirection: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    dispatch(
      fetchSystemList({
        listType: "CLIENT_TAG",
        context: "billing-tab-mapping-client",
        force: true,
      })
    );
    dispatch(
      fetchSystemList({
        listType: "ACCOUNTING_TAG",
        context: "billing-tab-mapping-acc",
        force: true,
      })
    );
    dispatch(fetchTabMappings({ activeOnly: false }));
  }, [dispatch]);

  const handleFilterReset = () => {
    setFilters({ ...EMPTY_TAB_FILTERS });
    setAppliedFilters({ ...EMPTY_TAB_FILTERS });
    setCurrentPage(1);
    dispatch(fetchTabMappings({ activeOnly: false }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const filterFields = [
    {
      type: "input",
      name: "search",
      label: "Search",
      value: filters.search || "",
      placeholder: "Search tag/account",
    },
    {
      type: "dropdown",
      name: "tag",
      label: "Client tag",
      value: filters.tag || "",
      options: tagFilterOptions,
      placeholder: "All",
      searchable: true,
      multiselect: false,
    },
    {
      type: "dropdown",
      name: "account",
      label: "Accounting",
      value: filters.account || "",
      options: accountFilterOptions,
      placeholder: "All",
      searchable: true,
      multiselect: false,
    },
  ];

  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedMappings = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [mappingToEdit, setMappingToEdit] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddMapping = () => {
    setMappingToEdit(null);
    setIsAddModalOpen(true);
  };

  const handleEditMapping = (item) => {
    setMappingToEdit(item);
    setIsAddModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    setMappingToDelete(item);
  };

  const refetchMappings = () => dispatch(fetchTabMappings({ activeOnly: false }));

  const handleConfirmDelete = async () => {
    if (!mappingToDelete?.id) return;
    try {
      await dispatch(deleteTabMappingApi(mappingToDelete.id)).unwrap();
      showSuccess("Mapping deleted");
      setMappingToDelete(null);
      await refetchMappings();
    } catch (e) {
      showError(e || "Failed to delete mapping");
    }
  };

  const handleSaveNewMapping = async ({ id, sourceTagId, targetTagId }) => {
    if (id) {
      await dispatch(updateTabMapping({ id, sourceTagId, targetTagId })).unwrap();
      showSuccess("Mapping updated");
    } else {
      await dispatch(createTabMapping({ sourceTagId, targetTagId })).unwrap();
      showSuccess("Mapping created");
      setCurrentPage(1);
    }
    await refetchMappings();
    setIsAddModalOpen(false);
    setMappingToEdit(null);
  };

  const handleCloseMappingModal = () => {
    setIsAddModalOpen(false);
    setMappingToEdit(null);
  };

  return (
    <>
      <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
        <div className="flex items-start justify-between">
          <BillingHeader />
          <AddButton onClick={handleAddMapping} tooltip="Add Mapping" className="mt-4" />
        </div>

        <GlobalFilter
          {...mappingListGlobalFilterProps}
          listingFilter={filters}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          fields={filterFields}
        />

        <div className="w-full relative flex flex-col bg-surface-primary shadow-sm">
          <div className="flex-1 p-2">
            <div className="overflow-x-auto">
              <table className="w-full mb-0 clients-table">
                <thead className="bg-surface-secondary">
                  <tr>
                    <SortableHeader
                      columnKey="tag"
                      label="Client Tag"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="account"
                      label="Accounting Category"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="action"
                      label="Actions"
                      sortable={false}
                      className="action-col"
                    />
                  </tr>
                </thead>

                <tbody className="relative">
                  {loading ? (
                    <tr aria-hidden="true">
                      <td colSpan={3} className="min-h-[240px] h-60 p-0 border-0" />
                    </tr>
                  ) : sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-6">
                        <Typography className="text-text-secondary">
                          No billing tag mappings found
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    paginatedMappings.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <button
                            type="button"
                            className="text-left w-full bg-transparent border-0 p-0 m-0 cursor-pointer"
                            onClick={() => handleEditMapping(item)}
                            aria-label={`Edit client tag mapping: ${item.tag}`}
                          >
                            <Typography variant="span1" className="font-medium">
                              {item.tag}
                            </Typography>
                          </button>
                        </td>

                        <td>
                          <Typography variant="span1">{item.account}</Typography>
                        </td>

                        <td className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <FloatingTooltip label="Edit">
                              <Button
                                size="sm"
                                className="btn-warning-new !w-7 !h-7 !p-0 flex items-center justify-center"
                                aria-label="Edit vendor tag mapping"
                                onClick={() => handleEditMapping(item)}
                              >
                                <RiPencilLine className="text-white text-[12px]" />
                              </Button>
                            </FloatingTooltip>
                            <FloatingTooltip label="Delete">
                              <Button
                                size="sm"
                                className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center"
                                aria-label="Delete client tag mapping"
                                onClick={() => handleDeleteClick(item)}
                              >
                                <RiDeleteBinFill className="text-white text-[12px]" />
                              </Button>
                            </FloatingTooltip>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="px-4 py-3 border-t bg-surface-secondary">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </ContentLoader>

      {isAddModalOpen && (
        <TabMappingAddModel
          onClose={handleCloseMappingModal}
          onSave={handleSaveNewMapping}
          sourceTagOptions={sourceTagOptions}
          accountingTagOptions={accountingTagOptions}
          existingMapping={mappingToEdit}
        />
      )}

      <DeleteConfirmModal
        isOpen={!!mappingToDelete}
        onClose={() => setMappingToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete client tag mapping"
        itemName={mappingToDelete ? `${mappingToDelete.tag} → ${mappingToDelete.account}` : ""}
      />
    </>
  );
};

export default TabMapping;
